/**
 * iter v33 r36 · Global tracker per i job "Migliora tutte".
 *
 * PROBLEMA da risolvere: prima il polling stava dentro PropertyDetail →
 * quando l'agente andava in Home o su un altro annuncio, il polling
 * moriva e il toast in cima allo schermo restava CONGELATO (es. "1/8
 * migliorate · 3 errori") facendo pensare che il batch fosse rotto,
 * quando invece il server continuava a lavorare. In piu' iOS Safari
 * mette in pausa i timer quando l'app va in background, aggravando il
 * problema.
 *
 * SOLUZIONE:
 * - Provider a livello App (sopravvive alla navigazione SPA)
 * - Polling globale via GET /photos/enhance-batch-active-mine ogni 2s
 * - `visibilitychange` listener: quando l'app torna in foreground fa un
 *   fetch immediato → l'utente vede subito lo stato aggiornato
 * - Espone {activeBatches, refresh, ...} ai consumer
 *
 * BatchTrayPill.jsx renderizza una piccola pill flottante in basso che
 * mostra il progresso e NON copre il menu (a differenza del toast top).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const BatchTrackerContext = createContext(null);

const POLL_MS = 2000;

export function BatchEnhanceTrackerProvider({ children }) {
  const { user } = useAuth() || {};
  // Mappa job_id → { property_id, property_title, done, total, errors, in_progress, status }
  const [activeBatches, setActiveBatches] = useState([]);
  // Traccio i job appena chiusi cosi' posso mostrare la notifica di
  // completamento anche se il polling successivo torna vuoto. Chiave:
  // job_id → { done, total, errors, status, at }
  const seenRef = useRef(new Map());
  // Handler opzionale per notifiche di completamento: registrato da
  // App.js e chiama toast.success/info. Non usiamo `toast` direttamente
  // qui per tenere il provider agnostico.
  const onCompleteRef = useRef(null);
  const registerOnComplete = useCallback((fn) => {
    onCompleteRef.current = fn;
  }, []);

  const fetchOnce = useCallback(async () => {
    if (!user) {
      setActiveBatches([]);
      return;
    }
    try {
      const { data } = await api.get("/photos/enhance-batch-active-mine");
      const jobs = Array.isArray(data?.jobs) ? data.jobs : [];
      // Rilevo transizioni running → completed/failed/cancelled: per
      // ogni job che ERA in `seenRef` (quindi era running) ma ora NON
      // e' nella lista attiva → il server l'ha chiuso, notifico.
      const currentIds = new Set(jobs.map((j) => j.job_id));
      const newlyClosed = [];
      seenRef.current.forEach((prev, jid) => {
        if (!currentIds.has(jid)) {
          newlyClosed.push({ jobId: jid, prev });
        }
      });
      // Aggiorno seen con lo stato piu' recente dei job ancora vivi.
      const nextSeen = new Map();
      jobs.forEach((j) => nextSeen.set(j.job_id, j));
      seenRef.current = nextSeen;
      setActiveBatches(jobs);
      // Per i job newlyClosed devo interrogare lo status finale per
      // ottenere il verdict (completed / credits_exhausted / failed /
      // cancelled) e informare il consumer.
      for (const { jobId, prev } of newlyClosed) {
        try {
          const { data: s } = await api.get(`/photos/enhance-batch-status/${jobId}`);
          if (onCompleteRef.current) {
            onCompleteRef.current({
              jobId,
              propertyId: prev.property_id,
              propertyTitle: prev.property_title,
              status: s.status,
              done: s.done || 0,
              total: s.total || 0,
              errors: s.errors || 0,
            });
          }
        } catch { /* silent */ }
      }
    } catch {
      // Se la fetch fallisce (offline o auth expired) mantieni lo stato
      // corrente per non far sparire la pill in modo brusco.
    }
  }, [user]);

  // Polling loop
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    let timer = null;
    const loop = async () => {
      if (cancelled) return;
      await fetchOnce();
      if (!cancelled) timer = setTimeout(loop, POLL_MS);
    };
    loop();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [user, fetchOnce]);

  // Foreground refresh: quando l'app torna visibile forzo un poll
  // immediato (iOS mette in pausa i timer JS in background).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchOnce();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchOnce]);

  const findByProperty = useCallback(
    (propertyId) => activeBatches.find((j) => j.property_id === propertyId) || null,
    [activeBatches],
  );

  const value = useMemo(
    () => ({ activeBatches, fetchOnce, findByProperty, registerOnComplete }),
    [activeBatches, fetchOnce, findByProperty, registerOnComplete],
  );

  return (
    <BatchTrackerContext.Provider value={value}>{children}</BatchTrackerContext.Provider>
  );
}

export function useBatchEnhanceTracker() {
  const ctx = useContext(BatchTrackerContext);
  if (!ctx) {
    throw new Error("useBatchEnhanceTracker deve essere usato dentro BatchEnhanceTrackerProvider");
  }
  return ctx;
}
